// ITEM A — **WHAT MEMBERS WROTE IN, AS ITS OWN COMPONENT.**
//
// It was the dashboard's fall-through arm in App.tsx — the branch every unrecognised view landed on
// — which is D6-3's roster problem wearing a different face: the surface that catches everything is
// also the surface nobody declared. It is a NAMED view now (`messages`, under IT Tickets, because a
// member writing in and a ticket are one support world by her order), and it moved out here because
// the round that added a section menu had to pay App.tsx's ceiling as it went.
//
// Hookless — the list, the filter and the member door all arrive as props — so a gate can CALL it
// and read the tree back (R7's entry requirement).
import { MemberDoor } from "./MemberDoor";
// **HER FOUR FILTERS, READ OFF THE SHARED DECLARATION** rather than typed here. `src/supportDesk.js`
// is the law the SERVER composes `stateLabel` from, so the strip she taps and the word on the row
// cannot spell a state two ways (R28/R29) — and on this surface the cost of two answers is a
// conversation that reads as answered while somebody waits.
import {
  SUPPORT_STATES, DESK_FOCUS_MISSING_NOTE,
  // T114-1 — which carrier this conversation may be answered on, and the two sentences for the two
  // ways there is no carrier. All three are the SHARED law's, so this screen cannot hold an opinion
  // the door disagrees with.
  supportReplyRoute, supportNoReplyNote, supportMailOffNote,
  // SD4's remaining half — the one word this transcript may say about a reply the room's gate held.
  SUPPORT_HELD_MARK,
} from "../supportDesk.js";

export type SupportTranscriptLine = { from: "house" | "member"; actor: string; body: string; at: string; held?: boolean };
export type OwnerContactMessage = {
  // >>> **THE FIELD IS THE PAYLOAD'S OWN NAME, AND IT WAS NOT (2026-08-14).** <<< This type said
  // `userId`, and `/api/owner/contact-messages` answers **`memberId`** — so `m.userId` was
  // `undefined` on EVERY row, the *no account on this address* sentence rendered on every
  // conversation in her desk, and the reply control rendered on NONE of them. **Her reply door has
  // never worked for anybody**, and the POST beneath it sent `memberId: undefined` besides, which
  // `supportReplyViolations` would have refused even if a control had drawn.
  //
  // Found by tracing ONE member's ticket (Ikeshia Vaughn, who has an account under the very address
  // the screen said had none). One spelling, and it is the SERVER's (R28/R29).
  id: string; memberId: string | null; reason: string; name: string; email: string;
  subject: string; message: string; createdAt: string;
  // ITEM 4b — what the desk knows about this conversation. Optional, because this row shape is older
  // than the desk and a payload that predates it must still render (R18: absent is not empty).
  state?: string;
  stateLabel?: string;
  ticketRef?: string;
  replies?: SupportTranscriptLine[];
};

// >>> **ITEM 4b (2026-08-13) — THE REPLY THE ROSTER HAD BEEN CLAIMING FOR THIS SURFACE ALL ALONG.**
// <<< `VIEWS` in `src/commandCenter.js` declares this view with `acts: ["open", "reply"]`, and this
// component rendered a list, a search box and an export link. **There was no reply control anywhere
// in it.** That is her own ITEM A clause — *"no dead read-only lists: if she can see it she can act
// on it or it does not render"* — breached in the one place it costs a member something: measured
// the same day, **21 people had written in, the oldest on 16 July, and none had been answered.**
//
// It stays HOOKLESS (R7's entry requirement, so a gate CALLS it): the open row, the draft text and
// the send all arrive as props, and the state lives with the dashboard that owns every other one.
export type SupportReply = {
  /** The conversation whose composer is open. `""` is none, which is a DECISION and not a default. */
  openId: string;
  draft: string;
  sending: boolean;
  /** What happened to the last send, in words. Empty until something has happened (R18). */
  note: string;
};

export function OwnerMessages({
  messages, filter, loading, shortDate, onFilter, onOpenMember,
  reply, onReplyOpen, onReplyDraft, onReplySend, mailArmed = false,
  state, onState, focused, focusMissing, onClearFocus,
}: {
  messages: OwnerContactMessage[];
  filter: string;
  loading: boolean;
  shortDate: (iso: string) => string;
  onFilter: (next: string) => void;
  onOpenMember: (id: string) => void;
  reply: SupportReply;
  onReplyOpen: (id: string) => void;
  onReplyDraft: (next: string) => void;
  onReplySend: (m: OwnerContactMessage) => void;
  /**
   * T114-1 — whether the mail rail can carry a reply to a writer with no account. It is a SERVER
   * fact, so it arrives as a prop; and it DEFAULTS TO FALSE, which is FREE-1's own default rule read
   * on a send path: a caller that says nothing gets the behaviour this surface had before the email
   * carrier existed, never a composer that would refuse.
   */
  mailArmed?: boolean;
  /** ITEM 4b clause 3 — which of her four filters is on. `""` is all of them. */
  state: string;
  onState: (next: string) => void;
  /** Clause 2 — she arrived here from a ticket, so this screen is showing that one conversation. */
  focused: boolean;
  /** ...and the ticket pointed at a conversation this screen does not hold (R18, never an empty desk). */
  focusMissing: boolean;
  onClearFocus: () => void;
}) {
  // Named here rather than spelled three times inside the row, so the three branches below are
  // provably asking ONE question about one conversation.
  const replyRoute = (m: OwnerContactMessage) => supportReplyRoute({ memberId: m.memberId, email: m.email });
  return (
    <>
      <div className="owner-bar">
        <p className="owner-page__note">Every message members send you, kept here so nothing is lost.</p>
        {/* **HER CLAUSE 3's FILTERS.** The roster was declared, the server shipped it down the wire
            and NOTHING RENDERED IT — `filterConversations` had no caller anywhere in the house, which
            is a law that is not a law (R28/R52/R138/R165). *All* is first and is not one of her four:
            a strip where every option narrows is one she cannot get out of. */}
        <div className="owner-deskfilter" role="group" aria-label="Filter conversations">
          <button type="button" className={state ? "owner-deskfilter__opt" : "owner-deskfilter__opt is-on"}
            aria-pressed={!state} onClick={() => onState("")}>All</button>
          {SUPPORT_STATES.map((s) => (
            <button key={s.key} type="button" title={s.note}
              className={state === s.key ? "owner-deskfilter__opt is-on" : "owner-deskfilter__opt"}
              aria-pressed={state === s.key} onClick={() => onState(s.key)}>{s.label}</button>
          ))}
        </div>
        {/* D6-4 — search on Messages, consistent with Members and Probes. Client-side, because the
            whole list is already loaded; a round trip to filter what is in the page is a spinner for
            nothing. */}
        <input className="owner-search" value={filter} onChange={(e) => onFilter(e.target.value)}
          placeholder="Search messages..." aria-label="Search messages" />
        <a className="button button--ghost" href="/api/owner/export?what=contact">Export CSV</a>
      </div>
      {loading ? <p className="owner-page__note">Loading...</p> : null}
      {/* **SHE CAME FROM A TICKET, SO THE SCREEN SAYS SO AND OFFERS THE WAY BACK.** A list that
          silently narrows to one row reads as a desk that lost everybody else. */}
      {focused ? (
        <p className="owner-deskfocus">Showing the one conversation this ticket came from.{" "}
          <button type="button" className="owner-deskfocus__all" onClick={onClearFocus}>Show all conversations</button>
        </p>
      ) : null}
      {/* ...and where the ticket points at a conversation this screen does not hold, she is TOLD.
          Answering that with an empty desk is the one sentence this surface may never say (R18/R64). */}
      {focusMissing ? (
        <p className="owner-note">{DESK_FOCUS_MISSING_NOTE}{" "}
          <button type="button" className="owner-deskfocus__all" onClick={onClearFocus}>Show all conversations</button>
        </p>
      ) : null}
      <div className="owner-messages">
        {messages.map((m) => (
          <article className="owner-message" key={m.id}>
            <header>
              <span className="owner-message__reason">{m.reason}</span>
              {/* Her four states, on the row, so *who is still waiting* is legible without opening
                  anything. `stateLabel` is the SERVER's own word off the shared roster — a label
                  composed here would be a second answer to what a state is called (R28/R29). */}
              {m.stateLabel ? <span className={`owner-message__state is-${m.state}`}>{m.stateLabel}</span> : null}
              {m.ticketRef ? <span className="owner-message__ticket">{m.ticketRef}</span> : null}
              <span className="owner-message__date">{shortDate(m.createdAt)}</span>
            </header>
            <h3>{m.subject}</h3>
            <p className="owner-message__from">
              <MemberDoor id={m.memberId} name={m.name} email={m.email} onOpen={onOpenMember} />
              {" "}&lt;{m.email}&gt;
            </p>
            <p className="owner-message__body">{m.message}</p>
            {/* **HER CLAUSE 2: NOTHING THE HOUSE SAYS TO A MEMBER IS INVISIBLE TO THE OWNER.** The
                transcript rides in the same row the list already carries, so opening a conversation
                is not a second read — and a conversation with a ticket names its NUMBER here, which
                is the surface clause 1 declared owed when the number shipped. */}
            {m.replies?.length ? (
              <ol className="owner-message__thread">
                {m.replies.map((r, i) => (
                  <li key={`${m.id}-${i}`} className={r.from === "house" ? "is-house" : "is-member"}>
                    <span className="owner-message__who">{r.from === "house" ? "The house" : m.name || "She"}</span>
                    <span className="owner-message__when">{shortDate(r.at)}</span>
                    {/* **LR19's RULING, ON THIS SIDE OF THE HOUSE: A HELD MESSAGE MAY NEVER LOOK
                        ORDINARY ON HER SCREEN.** Her reply joins this transcript whatever the room's
                        gate made of it — leaving it out would put the member the gate held back in
                        the void — and reading it here as an ordinary line is the exact defect she
                        reported in the room itself (*"she reads it like everyone sees it and has no
                        idea it is held"*). */}
                    {r.held ? <span className="owner-message__held">{SUPPORT_HELD_MARK}</span> : null}
                    <span className="owner-message__said">{r.body}</span>
                  </li>
                ))}
              </ol>
            ) : null}
            {/* >>> **T114-1 (2026-08-14) — A WRITER WITH NO ACCOUNT IS ANSWERED BY EMAIL, AND THIS
                BRANCH IS WHERE THAT USED TO STOP.** <<< It said *"No account on this address, so
                there is no inbox to answer into. Write to {m.email} directly."* — her own ITEM A
                clause satisfied by removing the control instead of building it, on a form that is
                open to visitors by its own migration's design.

                **THE DECISION IS THE SHARED LAW'S, NOT THIS COMPONENT'S** (`supportReplyRoute`), so
                the control she is offered and the carrier the door performs are one answer (R28/R29)
                — a desk that offers a reply the server refuses is R15's picker, one surface over.

                **AND THE RAIL BEING OFF IS ITS OWN STATE (R18):** a composer that draws and always
                refuses is the same dead control in the other direction, so where the email carrier is
                the only one and the rail is not armed she gets today's behaviour and the REASON. */}
            {replyRoute(m) === "" ? (
              <p className="owner-message__noreply">{supportNoReplyNote(m)}</p>
            ) : replyRoute(m) === "email" && !mailArmed ? (
              <p className="owner-message__noreply">{supportMailOffNote(m)}</p>
            ) : reply.openId === m.id ? (
              <div className="owner-message__reply">
                {/* **SHE IS TOLD WHERE IT IS ABOUT TO GO, BEFORE SHE TYPES IT.** The two carriers
                    read identically on this screen otherwise, and they are not the same act: an
                    inbox reply is inside the house and a mail is a letter to a stranger's address —
                    which is the one thing she would want to know before writing it. */}
                {replyRoute(m) === "email" ? (
                  <p className="owner-message__via">No account here, so this goes to {m.email} by email, in the house look.</p>
                ) : null}
                <textarea className="owner-message__draft" value={reply.draft} rows={4}
                  aria-label={`Reply to ${m.name || m.email}`}
                  placeholder="Answer as the house..."
                  onChange={(e) => onReplyDraft(e.target.value)} />
                <div className="owner-message__acts">
                  {/* `disabled` on an empty draft is the same refusal the door performs, so the
                      control and the server agree about what a sendable reply is (R28/R29). */}
                  <button type="button" className="button" disabled={reply.sending || !reply.draft.trim()}
                    onClick={() => onReplySend(m)}>{reply.sending ? "Sending..." : "Send as the house"}</button>
                  <button type="button" className="button button--ghost" disabled={reply.sending}
                    onClick={() => onReplyOpen("")}>Cancel</button>
                </div>
              </div>
            ) : (
              <button type="button" className="button button--ghost owner-message__open"
                onClick={() => onReplyOpen(m.id)}>Reply as the house</button>
            )}
            {reply.openId === m.id && reply.note ? <p className="owner-note">{reply.note}</p> : null}
          </article>
        ))}
        {/* **THREE REASONS FOR AN EMPTY LIST, AND ONLY ONE OF THEM IS "NOBODY HAS WRITTEN IN"
            (R18/R64).** Her filter narrowing to nothing and her search matching nothing are both
            answers about what she asked for; *no messages yet* is a claim about the house, and on
            this surface it is the claim that would tell her nobody is waiting. */}
        {!loading && !messages.length ? (
          <p className="owner-page__note">
            {state ? "Nothing is in that state right now."
              : filter.trim() ? "No message matches that."
              : "No messages yet."}
          </p>
        ) : null}
      </div>
    </>
  );
}
